import React, { useState, useEffect } from "react";
import { Container, Form, Button, Row, Col, Card, Alert, Spinner, ListGroup, Badge, ToggleButtonGroup, ToggleButton } from "react-bootstrap";
import { FaSave, FaTimes, FaEnvelope, FaUsers, FaGlobe } from "react-icons/fa";

import "../../../assets/css/dashboard.css";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useAuthFetch } from "../../context/AuthFetch";
import LeftNav from "../LeftNav";
import DashBoardHeader from "../DashBoardHeader";

const AdminMail = () => {
  const { auth, logout, refreshAccessToken, isLoading: authLoading, isAuthenticated } = useAuth();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [language, setLanguage] = useState('en'); // 'en' for English, 'hi' for Hindi
  
  // Form state
  const [formData, setFormData] = useState({
    admin_id: "",
    member_ids: [],
    subject: "",
    message: ""
  });
  
  // API data state
  const [members, setMembers] = useState([]);
  const [adminId, setAdminId] = useState("");
  const [adminName, setAdminName] = useState("");
  
  // Form validation and submission state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Translations for English and Hindi
  const translations = {
    en: {
      pageTitle: "Admin Mail",
      selectMembers: "Select Members",
      selectAll: "Select All",
      deselectAll: "Deselect All",
      subject: "Subject",
      message: "Message",
      enterSubject: "Enter email subject",
      enterMessage: "Enter your message",
      sendMail: "Send Mail",
      sending: "Sending...",
      cancel: "Cancel",
      noMembersFound: "No members found for this district.",
      membersRequired: "Please select at least one member",
      subjectRequired: "Subject is required",
      messageRequired: "Message is required",
      successMessage: "Mail sent successfully!",
      permissionDenied: "Permission denied. You may not have the required role to access this feature.",
      authError: "Authentication error. Please login again.",
      fetchError: "An error occurred while fetching member data",
      submitError: "Failed to send mail. Please try again.",
      authenticationRequired: "Authentication Required",
      needToLogin: "You need to be logged in to view this page.",
      goToLogin: "Go to Login",
      loading: "Loading...",
      fullName: "Full Name",
      email: "Email",
      district: "District",
      id: "ID",
      na: "N/A"
    },
    hi: {
      pageTitle: "व्यवस्थापक मेल",
      selectMembers: "सदस्य चुनें",
      selectAll: "सभी का चयन करें",
      deselectAll: "सभी का चयन रद्द करें",
      subject: "विषय",
      message: "संदेश",
      enterSubject: "ईमेल विषय दर्ज करें",
      enterMessage: "अपना संदेश दर्ज करें",
      sendMail: "मेल भेजें",
      sending: "भेजा जा रहा है...",
      cancel: "रद्द करें",
      noMembersFound: "इस जिले के लिए कोई सदस्य नहीं मिला।",
      membersRequired: "कृपया कम से कम एक सदस्य चुनें",
      subjectRequired: "विषय आवश्यक है",
      messageRequired: "संदेश आवश्यक है",
      successMessage: "मेल सफलतापूर्वक भेजा गया!",
      permissionDenied: "अनुमति नहीं। आपके पास इस सुविधा तक पहुंचने के लिए आवश्यक भूमिका नहीं हो सकती है।",
      authError: "प्रमाणीकरण त्रुटि। कृपया फिर से लॉगिन करें।",
      fetchError: "सदस्य डेटा लाने में एक त्रुटि हुई",
      submitError: "मेल भेजने में विफल। कृपया फिर से कोशिश करें।",
      authenticationRequired: "प्रमाणीकरण आवश्यक",
      needToLogin: "इस पृष्ठ को देखने के लिए आपको लॉग इन करना होगा।",
      goToLogin: "लॉगिन पर जाएं",
      loading: "लोड हो रहा है...",
      fullName: "पूरा नाम",
      email: "ईमेल",
      district: "जिला",
      id: "आईडी",
      na: "लागू नहीं"
    }
  };
  
  // Get current language translations
  const t = translations[language];

  // Check device width
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setSidebarOpen(width >= 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Fetch members on component mount
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchMembers();
    }
  }, [authLoading, isAuthenticated]);

  // Extract district from unique_id
  const getDistrictFromUniqueId = (uniqueId) => {
    // Prefer allocated_district from auth if available
    if (auth?.allocated_district) return auth.allocated_district;

    // Fallback: try to derive district from uniqueId if possible
    if (!uniqueId) return "";
    // If uniqueId contains district info in some format, implement parsing here.
    // Otherwise return empty string so caller can handle it.
    return "";
  };

  // Fetch members from API (similar to DistrictRegistration)
  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      // Make authenticated request using authFetch to get all members (no district filter)
      const response = await authFetch('https://mahadevaaya.com/ngoproject/ngoproject_backend/api/member-reg/', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        // Handle 401 Unauthorized specifically
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        // Handle 403 Forbidden specifically
        if (response.status === 403) {
          throw new Error('Permission denied. You may not have the required role to access this feature.');
        }
        throw new Error(`Failed to fetch members. Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("GET Members API Response:", result);

      // Handle different response formats
      if (Array.isArray(result)) {
        // Direct array response
        setMembers(result);
      } else if (result.success && result.data) {
        // Wrapped response object
        if (Array.isArray(result.data)) {
          setMembers(result.data);
        } else {
          setMembers([result.data]);
        }
      } else {
        throw new Error("Invalid response format or no members found");
      }
    } catch (error) {
      console.error("Error fetching members:", error);
      setErrors({ 
        fetch: error.message.includes('403') || error.message.includes('permission')
          ? t.permissionDenied
          : error.message.includes('401') || error.message.includes('authenticated') || error.message.includes('Session expired')
          ? t.authError
          : error.message || t.fetchError
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminMailData = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch('https://mahadevaaya.com/ngoproject/ngoproject_backend/api/admin-mail/', {
        method: 'POST'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || 'Failed to fetch admin mail data');
      }
      
      const data = await response.json();
      console.log('Admin mail data:', data);
      
      // Set admin ID and name
      if (data.admin_id) {
        setAdminId(data.admin_id);
        setFormData(prev => ({ ...prev, admin_id: data.admin_id }));
      }
      
      // Set admin name if available
      if (data.admin_name) {
        setAdminName(data.admin_name);
      } else if (auth?.name) {
        setAdminName(auth.name);
      }
      
    } catch (error) {
      console.error('Error fetching admin mail data:', error);
      setErrors({ fetch: error.message || "Failed to fetch admin mail data. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Show loading spinner while auth is loading
  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="main-content-dash d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t.loading}</span>
          </Spinner>
        </div>
      </div>
    );
  }

  // If not authenticated, show message and redirect
  if (!isAuthenticated) {
    return (
      <div className="dashboard-container">
        <div className="main-content-dash d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <Alert variant="warning">
              <Alert.Heading>{t.authenticationRequired}</Alert.Heading>
              <p>{t.needToLogin}</p>
              <Button variant="primary" onClick={() => navigate("/Login")}>
                {t.goToLogin}
              </Button>
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  // Handle checkbox changes
  const handleMemberSelection = (memberId) => {
    const idStr = String(memberId);
    setFormData(prev => {
      if (prev.member_ids.includes(idStr)) {
        // Remove member if already selected
        return {
          ...prev,
          member_ids: prev.member_ids.filter(id => id !== idStr)
        };
      } else {
        // Add member if not selected
        return {
          ...prev,
          member_ids: [...prev.member_ids, idStr]
        };
      }
    });
    
    // Clear error for this field if it exists
    if (errors.member_ids) {
      setErrors({
        ...errors,
        member_ids: null
      });
    }
  };

  // Handle Select All
  const handleSelectAll = () => {
    const allMemberIds = members.map(member => String(member.member_id || member.unique_id || member.id));
    setFormData(prev => ({
      ...prev,
      member_ids: allMemberIds
    }));
    
    // Clear error for this field if it exists
    if (errors.member_ids) {
      setErrors({
        ...errors,
        member_ids: null
      });
    }
  };

  // Handle Deselect All
  const handleDeselectAll = () => {
    setFormData(prev => ({
      ...prev,
      member_ids: []
    }));
    
    // Clear error for this field if it exists
    if (errors.member_ids) {
      setErrors({
        ...errors,
        member_ids: null
      });
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (formData.member_ids.length === 0) {
      newErrors.member_ids = t.membersRequired;
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = t.subjectRequired;
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t.messageRequired;
    }
    
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage("");
    
    try {
      // Create the payload with the required fields
      const payload = {
        admin_id: formData.admin_id || auth?.unique_id || adminId,
        member_ids: formData.member_ids,
        subject: formData.subject,
        message: formData.message
      };
      
      console.log('Sending payload:', payload);
      
      // Different API endpoints based on language
      let apiUrl;
      if (language === 'en') {
        // English API endpoint (actual endpoint)
        apiUrl = 'https://mahadevaaya.com/ngoproject/ngoproject_backend/api/admin-mail/';
      } else {
        // Hindi API endpoint (dummy for now)
        apiUrl = 'https://dummy-api-for-hindi.com/ngoproject/ngoproject_backend/api/admin-mail-hindi/';
      }
      
      // API call using authFetch to the correct endpoint
      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      // Handle 403 specifically
      if (response.status === 403) {
        throw new Error('You do not have permission to perform this action. Please contact your administrator.');
      }
      
      if (!response.ok) {
        // Handle specific error messages from the backend
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || 'Failed to send mail');
      }
      
      const result = await response.json();
      console.log('Success:', result);
      
      setSuccessMessage(t.successMessage);
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          admin_id: adminId,
          member_ids: [],
          subject: "",
          message: ""
        });
        setSuccessMessage("");
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Handle specific error cases
      if (error.message.includes('403') || error.message.includes('permission')) {
        setErrors({ 
          submit: t.permissionDenied
        });
      } else if (error.message.includes('authenticated') || error.message.includes('Session expired')) {
        setErrors({ 
          submit: t.authError
        });
        // Optionally redirect to login
        setTimeout(() => {
          navigate('/Login');
        }, 2000);
      } else {
        setErrors({ submit: error.message || t.submitError });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="dashboard-container">
        {/* Left Sidebar */}
        <LeftNav
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
          isTablet={isTablet}
        />

        {/* Main Content */}
        <div className="main-content-dash">
          <DashBoardHeader toggleSidebar={toggleSidebar} />

          <Container fluid className="dashboard-body dashboard-main-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h1 className="page-title">
                <FaEnvelope className="me-2" />
                {t.pageTitle}
              </h1>
              
              {/* Language Toggle */}
              <div className="language-toggle">
                <ToggleButtonGroup type="radio" name="language" value={language} onChange={(val) => setLanguage(val)}>
                  <ToggleButton id="lang-en" value="en" variant={language === 'en' ? 'primary' : 'outline-primary'}>
                    English
                  </ToggleButton>
                  <ToggleButton id="lang-hi" value="hi" variant={language === 'hi' ? 'primary' : 'outline-primary'}>
                    हिंदी
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
            </div>
            
            {successMessage && (
              <Alert variant="success" className="mb-4">
                {successMessage}
              </Alert>
            )}
            
            {errors.submit && (
              <Alert variant="danger" className="mb-4">
                {errors.submit}
              </Alert>
            )}
            
            {errors.fetch && (
              <Alert variant="danger" className="mb-4">
                {errors.fetch}
              </Alert>
            )}
            
            {isLoading ? (
              <div className="d-flex justify-content-center my-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t.loading}</span>
                </Spinner>
              </div>
            ) : (
              <Card className="p-4">
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={12} className="mb-3">
                      <Form.Group controlId="members">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <Form.Label className="mb-0">
                            <FaUsers className="me-1" />
                            {t.selectMembers}
                          </Form.Label>
                          {members.length > 0 && (
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleSelectAll}
                              >
                                {t.selectAll}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleDeselectAll}
                              >
                                {t.deselectAll}
                              </Button>
                            </div>
                          )}
                        </div>
                        {errors.member_ids && (
                          <div className="text-danger small mt-1">{errors.member_ids}</div>
                        )}
                        
                        {members.length > 0 ? (
                          <ListGroup className="mt-2">
                            {members.map((member) => (
                              <ListGroup.Item key={member.id || member.unique_id} className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  {(() => {
                                    const memberKey = String(member.member_id || member.unique_id || member.id);
                                    return (
                                      <Form.Check
                                        type="checkbox"
                                        id={`member-${memberKey}`}
                                        checked={formData.member_ids.includes(memberKey)}
                                        onChange={() => handleMemberSelection(memberKey)}
                                        className="me-3"
                                      />
                                    );
                                  })()}
                                  <div>
                                    <div className="fw-bold">{member.full_name || member.name || t.na}</div>
                                    <div className="text-muted small">{member.email || t.na}</div>
                                    <Badge bg="secondary" className="mt-1">{member.district || member.allocated_district || t.na}</Badge>
                                  </div>
                                </div>
                                <div className="text-muted small">{member.member_id || member.id || member.unique_id}</div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <Alert variant="info" className="mt-2">
                            {t.noMembersFound}
                          </Alert>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={12} className="mb-3">
                      <Form.Group controlId="subject">
                        <Form.Label>{t.subject}</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          isInvalid={!!errors.subject}
                          placeholder={t.enterSubject}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.subject}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={12} className="mb-3">
                      <Form.Group controlId="message">
                        <Form.Label>{t.message}</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          isInvalid={!!errors.message}
                          placeholder={t.enterMessage}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <div className="d-flex justify-content-end">
                    <Button 
                      variant="secondary" 
                      className="me-2"
                      onClick={() => navigate('/DistrictDashboard')}
                    >
                      <FaTimes className="me-1" /> {t.cancel}
                    </Button>
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={isSubmitting}
                      className="d-flex align-items-center btn-lg"
                    >
                      <FaSave className="me-1" /> 
                      {isSubmitting ? t.sending : t.sendMail}
                    </Button>
                  </div>
                </Form>
              </Card>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};
 
export default AdminMail;